import { useEffect } from 'react';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  breadcrumbs?: BreadcrumbItem[];
}

const DEFAULT_TITLE = 'Aura Traveler — Intelligent Luxury Travel Design';

function setMetaTag(property: string, content: string, isName = false) {
  const attr = isName ? 'name' : 'property';
  let tag = document.querySelector<HTMLMetaElement>(`meta[${attr}="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, property);
    document.head.appendChild(tag);
  }
  tag.content = content;
}

function injectJsonLd(id: string, data: object) {
  let script = document.querySelector<HTMLScriptElement>(`script[data-seo-jsonld="${id}"]`);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', id);
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function removeJsonLd(id: string) {
  const script = document.querySelector(`script[data-seo-jsonld="${id}"]`);
  if (script) script.remove();
}

export function useSEO({ title, description, canonical, ogImage, breadcrumbs }: SEOProps) {
  useEffect(() => {
    document.title = title;

    // Standard meta
    setMetaTag('description', description, true);

    // Canonical
    let canon = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement('link');
      canon.rel = 'canonical';
      document.head.appendChild(canon);
    }
    canon.href = canonical || window.location.href;

    // Open Graph
    setMetaTag('og:title', title);
    setMetaTag('og:description', description);
    setMetaTag('og:url', canonical || window.location.href);
    if (ogImage) {
      setMetaTag('og:image', ogImage);
    }

    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image', true);
    setMetaTag('twitter:title', title, true);
    setMetaTag('twitter:description', description, true);
    if (ogImage) {
      setMetaTag('twitter:image', ogImage, true);
    }

    // JSON-LD WebPage schema
    const pageUrl = canonical || window.location.href;
    injectJsonLd('webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url: pageUrl,
    });

    // JSON-LD BreadcrumbList schema (for subpages)
    if (breadcrumbs && breadcrumbs.length > 0) {
      injectJsonLd('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      });
    }

    return () => {
      document.title = DEFAULT_TITLE;
      removeJsonLd('webpage');
      removeJsonLd('breadcrumb');
    };
  }, [title, description, canonical, ogImage, breadcrumbs]);
}
